import { RequestDetailsHeader, RequestDetailsSection } from "../RequestDetailsContent";
import {
  RequestCardReason,
  RequestInfoItem,
  RequestInfoGrid,
  RequestCardSection,
  RequestCardHeader,
  RequestStatusStrip,
} from "../RequestCardContent";
import { useState, useMemo, useEffect } from "react";

import {
  CheckCircle2,
  XCircle,
  RotateCcw,
  Clock,
  User,
  Calendar,
  AlertTriangle,
  Eye,
  CloudLightning,
  CloudOff,
} from "lucide-react";
import { Card, CardContent } from "../../../ui/card";
import { Badge } from "../../../ui/badge";
import { CLOUD_UNCONFIGURED_STATUS, formatRequestStatus, getStatusColor } from "../utils";
import { Button } from "../../../ui/button";
import { Dialog, DialogContent } from "../../../ui/dialog";
import { AwsDataAccess, ResourceRequestsProps } from "../resourceRequestInterface";
import ResourceRequestToolbar from "../ResourceRequestToolbar";
import { changeAWSStatus, getAWSNotificationAPI, getAWSNotificationCountAPI, revokeRequest } from "../Api/resourcerequestapi";
import { myLocalStorage } from "../../../StorageHelper";
import useUserStore from "../../../../services/userStore";
import { format } from "date-fns";
import { formatDate } from "../../formatTimeAgo";
import { RESOURCE_REQUEST_POLL_MS } from "../resourceRequestPolling";
import { Skeleton } from "../../interfaces/CommonInterfaces";
import MultiApprovalProgressPanel from "../../MultiApprovals/MultiApprovalProgressPanel";
import MultiApprovalGroupedHistory from "../../MultiApprovals/MultiApprovalGroupedHistory";
import MultiApprovalListSummary from "../../MultiApprovals/MultiApprovalListSummary";
import { isMultiApprovalRequest } from "../../MultiApprovals/Api/multiapprovalapi";
import { useMultiApprovalWorkflow } from "../hooks/useMultiApprovalWorkflow";

export default function AWSResourceRequests({
  computerName = "",
  searchTerm = "",
  statusFilter = "",
  privilegeFilter = "all",
  startDate,
  endDate,
  activeTab,
  onSearchChange
}: ResourceRequestsProps) {
  const [selectedRequest, setSelectedRequest] = useState<AwsDataAccess | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<AwsDataAccess[]>([]);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const tenantName = useMemo(
    () => myLocalStorage.getItem("latestTenant").tenantName || "",
    []
  );

  const { email, role } = useUserStore((state) => state.user);
  const [loadingIds, setLoadingIds] = useState<Set<number>>(new Set());

  const {
    detailsWorkflow,
    openDetailsWorkflow,
    closeDetailsWorkflow,
    canUserApprove,
    canUserDecline,
    canUserDeclineDetails,
    getWorkflowForRequest,
  } = useMultiApprovalWorkflow(notifications);

  const handleViewDetails = (request: AwsDataAccess) => {
    setSelectedRequest(request);
    setIsDetailsOpen(true);
    openDetailsWorkflow(request);
  };



  const getStatusIcon = (status: string) => {
    switch (status) {
      case "PENDING":
        return <Clock className="w-4 h-4" />;
      case "APPROVED":
        return <CheckCircle2 className="w-4 h-4" />;
      case "DECLINED":
        return <XCircle className="w-4 h-4" />;
      case "REVOKED":
        return <RotateCcw className="w-4 h-4" />;
      case "EXPIRED":
        return <AlertTriangle className="w-4 h-4" />;
      case CLOUD_UNCONFIGURED_STATUS:
        return <CloudOff className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const handleRequest = async (
    status: string,
    id: number,
    notification: AwsDataAccess
  ) => {
    setLoadingIds((current) => new Set(current).add(id));
    try {
      let payload = {
        accessId: id,
        requestStatus: status,
        accessType: notification.accessType,
        arn: notification.accessARN,
        name: notification.accessName,
        userName: notification.awsUsername,
        policy: notification.selectedPolicies,
      }
      await changeAWSStatus(tenantName, payload);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingIds((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    }

    await getAWSNotification(false);
  };

  const handleRevoke = async (accessId: number) => {
    setLoadingIds((current) => new Set(current).add(accessId));
    try {
      const revoked = await revokeRequest("aws", accessId);
      if (revoked !== true) {
        throw new Error("Failed to cancel or revoke AWS request");
      }
      setIsDetailsOpen(false);
      await getAWSNotification(false);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingIds((current) => {
        const next = new Set(current);
        next.delete(accessId);
        return next;
      });
    }
  };

  const fetchAWSCount = async () => {
    const response = await getAWSNotificationCountAPI(
      computerName,
      searchTerm,
      statusFilter.toUpperCase(),
      startDate ? format(startDate, "yyyy-MM-dd") : "",
      endDate ? format(endDate, "yyyy-MM-dd") : "",
      tenantName,
      page,
      email,
      role,
        privilegeFilter
      )
    setTotalItems(response || 0);
  }

  const getAWSNotification = async (isFirst = true) => {
    setIsLoading(isFirst);
    try {
      const response: AwsDataAccess[] = await getAWSNotificationAPI(
        computerName,
        searchTerm,
        statusFilter.toUpperCase(),
        startDate ? format(startDate, "yyyy-MM-dd") : "",
        endDate ? format(endDate, "yyyy-MM-dd") : "",
        tenantName,
        page - 1,
        email,
        role,
      privilegeFilter
    );
      setNotifications(response);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAWSCount();
    getAWSNotification();
    if (activeTab === "aws") {
      const interval = setInterval(() => {
        fetchAWSCount();
        getAWSNotification(false);
      }, RESOURCE_REQUEST_POLL_MS)
      return () => clearInterval(interval)
    }
  }, [
    computerName,
    searchTerm,
    statusFilter,
    privilegeFilter,
    startDate,
    endDate,
    tenantName,
    page,
    email,
    role,
    activeTab
  ]);
  const totalPages = Math.ceil(totalItems / 15);

  return (
    <div className="resource-request-list space-y-1">
      {onSearchChange && (
        <ResourceRequestToolbar
          page={page}
          totalItems={totalItems}
          onPageChange={setPage}
          searchTerm={searchTerm}
          onSearchChange={(value) => {
            setPage(1);
            onSearchChange(value);
          }}
        />
      )}
      {isLoading ? (
        <>
          {[...Array(1)].map((_, idx) => (
            <Card key={idx} className="request-card border-slate-200 dark:border-slate-700">
              <CardContent className="request-card-content p-3 space-y-3">
                <div className="flex justify-between items-center">
                  <Skeleton className="w-40 h-4" />
                  <Skeleton className="w-12 h-4 ml-auto" />
                </div>
                <Skeleton className="w-64 h-3" />
                <Skeleton className="w-32 h-3" />
                <RequestInfoGrid className="grid-cols-2 md:grid-cols-4">
                  <Skeleton className="w-full h-3" />
                  <Skeleton className="w-full h-3" />
                  <Skeleton className="w-full h-3" />
                  <Skeleton className="w-full h-3" />
                </RequestInfoGrid>
              </CardContent>
            </Card>
          ))}
        </>
      ) : notifications.length > 0 ? (
        <>

          {/* Request Cards */}
          {notifications.map((request) => (
            <Card
              key={request.id}
              className="request-card border-slate-200 dark:border-slate-700"
              data-testid={`aws-request-card-${request.id}`}
            >
              <RequestStatusStrip status={request.requestStatus} />
              <CardContent className="request-card-content p-2">
                <div className="flex items-start gap-3">
                  <div className="request-card-body flex-1 space-y-1">
                    {/* Header */}
                    <RequestCardHeader
                      icon={<CloudLightning className="w-3.5 h-3.5 text-orange-500" />}
                      title={<>{request.accessName} Access</>}
                      status={
                        <Badge
                          className={`border text-xs px-1.5 py-0.5 ${getStatusColor(request.requestStatus)}`}
                        >
                          <div className="flex items-center gap-1">
                            {getStatusIcon(request.requestStatus)}
                            {formatRequestStatus(request.requestStatus)}
                          </div>
                        </Badge>
                      }
                      subtitle={
                        <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-300">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(request.createdAt)}
                          </div>
                          <div className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {request.email}
                          </div>
                        </div>
                      }
                      actions={
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewDetails(request)}
                          className="text-xs px-2 py-1 h-auto"
                          data-testid={`button-details-aws-${request.id}`}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Details
                        </Button>
                      }
                    />

                    {/* Content */}
                    <RequestInfoGrid className="grid-cols-4">
                      <RequestInfoItem label="Access Name">
                        <div className="font-medium text-slate-700 dark:text-slate-200">
                          {request.accessName}
                        </div>
                      </RequestInfoItem>
                      <RequestInfoItem label="Policies">
                        <div className="font-medium text-slate-700 dark:text-slate-200">
                          {request.selectedPolicies}
                        </div>
                      </RequestInfoItem>
                      <RequestInfoItem label="Access Type">
                        <div className="font-medium text-slate-700 dark:text-slate-200">
                          {request.accessType}
                        </div>
                      </RequestInfoItem>
                      <RequestInfoItem label="Duration">
                        <div className="font-medium text-slate-700 dark:text-slate-200">
                          {request.privilegeLevel === "PERMANENT"
                            ? "Permanent"
                            : `${request.selectedTime} min`}
                        </div>
                      </RequestInfoItem>
                    </RequestInfoGrid>
                    {request.schedules && request.schedules.length > 0 && (
                      <RequestCardSection variant="schedule">
                        <RequestInfoGrid className="grid-cols-2" context>
                          <RequestInfoItem label="Start Time">
                            <p className="text-blue-800 dark:text-blue-200">
                              {request.schedules[0]?.scheduledStartTime
                                ? formatDate(request.schedules[0].scheduledStartTime)
                                : "—"}
                            </p>
                          </RequestInfoItem>

                          <RequestInfoItem label="End Time">
                            <p className="text-blue-800 dark:text-blue-200">
                              {request.schedules[0]?.scheduledEndTime
                                ? formatDate(request.schedules[0].scheduledEndTime)
                                : "—"}
                            </p>
                          </RequestInfoItem>
                        </RequestInfoGrid>
                      </RequestCardSection>
                    )}

                    {((request.approvalEmailId && request.approvalEmailId.length > 0) ||
                      request.revokedBy) && (
                      <RequestCardSection variant="history">
                        <div className="space-y-1 text-sm">
                          {request.approvalEmailId &&
                            request.approvalEmailId.length > 0 && (
                              <p className="text-green-700 dark:text-green-300">
                                ✓ Approved by {request.approvalEmailId[0]} on{" "}
                                {formatDate(request.createdAt)}
                              </p>
                            )}

                          {request.revokedBy && (
                            <p className="text-purple-700 dark:text-purple-300">
                              ⊝ Revoked by {request.revokedBy} on{" "}
                              {formatDate(request.createdAt ?? new Date().toISOString())}
                            </p>
                          )}
                        </div>
                      </RequestCardSection>
                    )}
                    <RequestInfoGrid className="grid-cols-2 md:grid-cols-3" context>
                      <RequestInfoItem label="Account Id">
                        <p className="font-mono text-xs text-green-800 dark:text-green-200">
                          {request.awsAccountId}
                        </p>
                      </RequestInfoItem>
                      <RequestInfoItem label="User Name">
                        <p className="text-xs text-green-800 dark:text-green-200">
                          {request.awsUsername}
                        </p>
                      </RequestInfoItem>
                    </RequestInfoGrid>

                    {(() => {
                      const workflow = getWorkflowForRequest(request.id);
                      return isMultiApprovalRequest(workflow) ? (
                        <MultiApprovalListSummary workflow={workflow} />
                      ) : null;
                    })()}

                    {/* Actions */}
                    <div className="request-card-footer flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700">
                      <RequestCardReason>{request.requestMessage}</RequestCardReason>
                      <div className="flex items-center gap-2">
                        {request.requestStatus === "PENDING" && (
                          <>
                            {role !== "TENANT_USER" ? (
                              <>
                                <Button
                                  disabled={
                                    loadingIds.has(request.id) ||
                                    !canUserApprove(request.id)
                                  }
                                  size="sm"
                                  className="text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                                  onClick={() =>
                                    handleRequest("APPROVED", request.id, request)
                                  }
                                >
                                  {loadingIds.has(request.id) ? (
                                    <>
                                      <svg
                                        className="animate-spin w-4 h-4 mr-1 text-white"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                      >
                                        <circle
                                          className="opacity-25"
                                          cx="12"
                                          cy="12"
                                          r="10"
                                          stroke="currentColor"
                                          strokeWidth="4"
                                        ></circle>
                                        <path
                                          className="opacity-75"
                                          fill="currentColor"
                                          d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                                        ></path>
                                      </svg>
                                      Approving...
                                    </>
                                  ) : (
                                    <>
                                      <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                                    </>
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  disabled={
                                    loadingIds.has(request.id) ||
                                    !canUserDecline(request.id)
                                  }
                                  className="disabled:opacity-50"
                                  onClick={() =>
                                    handleRequest("DECLINE", request.id, request)
                                  }
                                >
                                  <XCircle className="w-4 h-4 mr-1" /> Decline
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  className="text-white bg-green-600 hover:bg-green-700"
                                  onClick={() => setIsDetailsOpen?.(false)}
                                >
                                  <CheckCircle2 className="w-4 h-4 mr-1" /> Pending
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={loadingIds.has(request.id)}
                                  className="text-purple-600 border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                                  onClick={() => handleRevoke(request.id)}
                                >
                                  <RotateCcw className="w-4 h-4 mr-1" /> Cancel Request
                                </Button>
                              </>
                            )}
                          </>
                        )}

                        {request.requestStatus === "APPROVED" && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={loadingIds.has(request.id)}
                            className="text-purple-600 border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                            onClick={() => handleRevoke(request.id)}
                            data-testid={`button-revoke-aws-${request.id}`}
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Revoke
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </>
      ) : (
        <Card className="request-card request-empty-state border-slate-200 dark:border-slate-700">
          <CardContent className="request-card-content p-12 text-center">
            <CloudLightning className="w-12 h-12 mx-auto mb-4 text-orange-300 dark:text-orange-600" />
            <h3 className="text-lg font-medium text-slate-600 dark:text-slate-300 mb-2">No AWS requests found</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              No access requests found for AWS resources
            </p>
          </CardContent>
        </Card>
      )}

      {!onSearchChange && totalPages > 1 && (
        <div className="flex items-center justify-between py-4">
          <div className="text-sm text-slate-600 dark:text-slate-300">
            Showing {(page - 1) * 15 + 1}–{Math.min(page * 15, totalItems)} of {totalItems}
          </div>

          <div className="flex items-center space-x-2">
            {/* Previous Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              data-testid="button-prev-page-onprem"
            >
              Previous
            </Button>

            {/* Dynamic Page Numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const startPage = Math.max(1, Math.min(page - 2, totalPages - 4));
              const pageNum = startPage + i;

              return (
                <Button
                  key={pageNum}
                  variant={pageNum === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPage(pageNum)}
                  className={pageNum === page ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
                  data-testid={`page-onprem-${pageNum}`}
                >
                  {pageNum}
                </Button>
              );
            })}

            {/* Next Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              data-testid="button-next-page-onprem"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Details Modal */}
      <Dialog
        open={isDetailsOpen}
        onOpenChange={(open) => {
          setIsDetailsOpen(open);
          if (!open) {
            closeDetailsWorkflow();
          }
        }}
      >
        <DialogContent className="request-details-dialog">
          <RequestDetailsHeader
            icon={<CloudLightning />}
            title="AWS Resource Request Details"
          >
            Complete details for the AWS resource access request
          </RequestDetailsHeader>
          {selectedRequest && (
            <>
              <div className="request-details-body">
                <RequestDetailsSection title="Request information" variant="request">
                  <RequestInfoGrid className="request-details-grid">
                    <RequestInfoItem label="Request ID">
                      <p className="text-slate-800 dark:text-slate-100">
                        {selectedRequest.id}
                      </p>
                    </RequestInfoItem>
                    <RequestInfoItem label="Status">
                      <p className="text-slate-800 dark:text-slate-100">
                        {formatRequestStatus(selectedRequest.requestStatus)}
                      </p>
                    </RequestInfoItem>
                    <RequestInfoItem label="Requested By">
                      <p className="text-slate-800 dark:text-slate-100">
                        {selectedRequest.email}
                      </p>
                    </RequestInfoItem>
                    <RequestInfoItem label="Created">
                      <p className="text-slate-800 dark:text-slate-100">
                        {formatDate(selectedRequest.createdAt)}
                      </p>
                    </RequestInfoItem>
                  </RequestInfoGrid>
                </RequestDetailsSection>
                <RequestDetailsSection title="AWS account and access">
                  <RequestInfoGrid className="request-details-grid">
                    <RequestInfoItem label="User Name">
                      <p className="text-slate-800 dark:text-slate-100">
                        {selectedRequest.awsUsername}
                      </p>
                    </RequestInfoItem>
                    <RequestInfoItem label="AWS Account">
                      <p className="text-slate-800 dark:text-slate-100">
                        {selectedRequest.awsAccountId}
                      </p>
                    </RequestInfoItem>
                    <RequestInfoItem label="Access Name">
                      <p className="text-slate-800 dark:text-slate-100">
                        {selectedRequest.accessName}
                      </p>
                    </RequestInfoItem>
                    <RequestInfoItem label="Policies">
                      <p className="text-slate-800 dark:text-slate-100">
                        {selectedRequest.selectedPolicies}
                      </p>
                    </RequestInfoItem>
                    <RequestInfoItem label="Access Type">
                      <p className="text-slate-800 dark:text-slate-100">
                        {selectedRequest.accessType}
                      </p>
                    </RequestInfoItem>
                  </RequestInfoGrid>
                </RequestDetailsSection>
                <RequestDetailsSection title="Justification" variant="justification">
                  <p className="text-slate-800 dark:text-slate-100 mt-1">
                    {selectedRequest.requestMessage}
                  </p>
                </RequestDetailsSection>
                {detailsWorkflow?.groupedApprovals?.some(
                  (group) => group.approvals.length > 0,
                ) && (
                  <div className="request-details-workflow">
                    <MultiApprovalGroupedHistory
                      groupedApprovals={detailsWorkflow.groupedApprovals}
                    />
                  </div>
                )}
                {selectedRequest.requestStatus === "PENDING" &&
                  isMultiApprovalRequest(detailsWorkflow) && (
                    <div className="request-details-workflow">
                      <MultiApprovalProgressPanel
                        ruleProgress={detailsWorkflow.ruleProgress}
                      />
                    </div>
                  )}
              </div>
              <div className="request-details-footer">
                {selectedRequest.requestStatus === "PENDING" && (
                  <div className="request-details-actions">
                    <Button variant="outline" onClick={() => setIsDetailsOpen(false)}>
                      Close
                    </Button>
                    {role === "TENANT_USER" ? (
                      <Button
                        variant="outline"
                        disabled={loadingIds.has(selectedRequest.id)}
                        className="text-purple-600 border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                        onClick={() => handleRevoke(selectedRequest.id)}
                      >
                        <RotateCcw className="w-4 h-4 mr-1" /> Cancel Request
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="destructive"
                          disabled={
                            loadingIds.has(selectedRequest.id) || !canUserDeclineDetails()
                          }
                          className="disabled:opacity-50"
                          onClick={() => {
                            setIsDetailsOpen(false);
                            handleRequest("DECLINE", selectedRequest.id, selectedRequest);
                          }}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Decline
                        </Button>
                        <Button
                          disabled={
                            loadingIds.has(selectedRequest.id) ||
                            !canUserApprove(selectedRequest.id)
                          }
                          onClick={() => {
                            handleRequest("APPROVED", selectedRequest.id, selectedRequest);
                          }}
                          className="bg-green-600 hover:bg-green-700 disabled:opacity-50"
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                      </>
                    )}
                  </div>
                )}
                {selectedRequest.requestStatus === "APPROVED" && (
                  <div className="request-details-actions">
                    <Button variant="outline" onClick={() => setIsDetailsOpen(false)}>
                      Close
                    </Button>
                    <Button
                      variant="outline"
                      disabled={loadingIds.has(selectedRequest.id)}
                      className="text-purple-600 border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                      onClick={() => handleRevoke(selectedRequest.id)}
                    >
                      <RotateCcw className="w-4 h-4 mr-1" /> Revoke
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
